import React from 'react';
import LanguageSelectionImage from '../img/case-studies/cw-max-languageSelection.jpg';
import maxLogoImage from '../img/case-studies/MaxLogo_white.svg';
import maxShoppingUserFlowImage from '../img/case-studies/cw-max-shoppingFlowchart.jpg';
import maxDesignProcessImage from '../img/case-studies/cw-max-designProcess.jpg';
import maxCommissionReportImage from '../img/case-studies/cw-max-commissionReport.jpg';
import maxCommissionReportImage2 from '../img/case-studies/cw-max-commissionReport2.jpg';

function Max() {
  const projectComponents = [
    { title: "The Max Website", description: "Max's primary hub for information and product sales" },
    { title: "The Virtual Office", description: "Online portal for Max Associates to manage their business" },
    { title: "The Max App", description: "A prospecting and contact management tool for Max Associates" }
  ];

  return (
    <section id="caseStudy_max" className="bg-light">
      <div className="bannerArea">
        <div className="bannerContent">
          <img alt="Max Logo" src={maxLogoImage} />
        </div>
      </div>
      <div className="container">
        <div className="row py-5 px-4">
          <div className="col">
            <h2>About</h2>
            <p>
              Max International sells glutathione-enhancing wellness products through a network marketing model: independent Associates sell to customers and build teams of their own, earning commissions on both.
            </p>
            <h2>The Challenge</h2>
            <p>
              Max operates in more than a dozen countries. After several years in different roles at the company, I was tasked with redesigning their digital platforms (the marketing website, the Associate back office, and a mobile app) to improve the buying and selling experience across all three. A single purchase touched all three systems at once: a customer's order on the website had to be attributed to the right Associate, show up correctly in that Associate's commission reports in the Virtual Office, and trigger a follow-up prompt in the app, all while accounting for which products were even available in that customer's market.
            </p>
          </div>
          <div className="col-12 col-md-3">
            <section id="projectComponents" className=" pt-5 pt-md-0 px-0 px-md-4">
            <div>
              <h3>Project Components:</h3>
              <ul>
                <li>The Company Website</li>
                <li>The Virtual Office</li>
                <li>Max Mobile App</li>
              </ul>
            </div>
            <h3>My Role</h3>
            <ul>
              <li>Research and analysis</li>
              <li>User Flows</li>
              <li>App Design</li>
                <li>Art Direction</li>
                <li>Prototyping</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="process-timeline">
              {projectComponents.map((component, index) => (
                <div key={index} className="process-step">
                  <div className="step-number">{index + 1}</div>
                  <h3>{component.title}</h3>
                  <p>{component.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <section>
        <div className="container ">
        <div className="row py-5 px-4 bg-white--rounded">
          <div className="col-12 col-md-6"> 
            <h2>The Process</h2>
            <h3>Research and Analysis</h3>
            <p>
              Research started with user interviews, a competitive audit, and a full walkthrough of Max's existing systems. That surfaced the purchase flow as the trickiest problem to solve.
            </p>
            <p>
              It needed separate paths for new and existing customers, had to attribute every sale to the correct Associate for commission tracking, and still had to stay simple enough that a first-time buyer wouldn't get lost in the mechanics behind it.
            </p>
          </div>
          <div className="col-12 col-md-6">
            <img className="mt-5 my-md-0" alt="Max Shopping User Flow" src={maxShoppingUserFlowImage} />
          </div>
        </div>
        <div className="row py-5 px-4">
          <div className="col-12">
            <h3>Design and Prototyping</h3>
            <p>
              From there I worked through thumbnail sketches and low-fidelity prototypes before moving to high-fidelity designs the development team could build from. The same process repeated across the website, the Virtual Office, and the app.
            </p>
            <img className="img-rounded my-5" alt="The design process featuring thumbnail sketches and prototypes" src={maxDesignProcessImage} />
          </div>
        </div>
        <div className="row pb-5 px-4">
          <div className="col-12 col-md-5">
            <img className="img-rounded my-5 my-md-0" alt="Example of a language selection page" src={LanguageSelectionImage} />
          </div>
          <div className="col-12 col-md-7 pb-5">
            <h3>Adapting to Global Markets</h3>
            <p>
              Associates needed tools for running their business (events, commission reports, team management) while customers mostly needed product information and a fast path to purchase. Layered on top of that, product availability varied by market, so content had to adapt by region without turning into a maintenance burden. To address this, we implemented:
            </p>
            <ul>
              <li>A <strong>modular content management system</strong> that adapts to regional requirements</li>
              <li>A <strong>streamlined enrollment flow</strong> that balances sponsor ID requirements with user convenience</li>
              <li>Market-specific landing pages with <strong>language selection</strong></li>
            </ul>
          </div>
        </div>
        <div className="row pb-5 px-4">
          <div className="col-12 col-md-6">
            <h2>The Virtual Office</h2>
            <p>
              The Virtual Office redesign focused on what Associates actually do day to day: check their numbers, manage their team, and find training. That meant:
            </p>
            <ul>
              <li><strong>Consolidated navigation:</strong> fewer menu items, organized around what Associates do rather than how the backend was structured</li>
              <li><strong>Reports Associates could actually read:</strong> commission and team data shown with context instead of raw numbers on a page</li>
              <li><strong>Modular training:</strong> shorter, focused courses instead of one long onboarding track</li>
            </ul>
          </div>
          <div className="col-12 col-md-6">
            <div id="commissionReportImgContainer" className="size--3x2">
            <img className="commissionReportImg2 img-rounded" alt="Commission Report Example" src={maxCommissionReportImage2} />
              <img className="commissionReportImg img-rounded" alt="Commission Report Example" src={maxCommissionReportImage} />
            </div>
          </div>
        </div>
      </div>
      </section>
      <div className="highlightSection">
        <div className="container">
          <div className="row py-5 px-4">
            <div className="col-12 col-lg-5 bg-white--rounded py-4 px-0 px-lg-4">
              <h2>The Max App</h2>
              <p>
                The Max App gave Associates the pieces of the Virtual Office they needed while away from a desktop:
              </p>
              <ul>
                <li>One-tap <strong>content sharing</strong> for customer presentations</li>
                <li>Prospect tracking with automated <strong>follow-up reminders</strong></li>
                <li>Quick access to <strong>training and sales resources</strong></li>
              </ul>
              <p className="text-center pt-3">
                <a className="button-secondary" href="https://xd.adobe.com/view/3027491b-1af1-412a-beeb-2334fc5a5da4-6574/" target="_blank" rel="noreferrer">View the Prototype</a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row py-5 px-4">
          <div className="col-12">
            <h2>Reflections and Learnings</h2>
            <p>
              Designing across three connected systems meant every decision had ripple effects: a change to the purchase flow on the website wasn't really finished until I'd traced it through commission reporting and the app's follow-up prompts too. That habit, following a change all the way through the systems it touches rather than treating each one as its own project, is something I carried into every project since.
            </p>
            <p>
              Working this closely with engineering mattered more here than it would on a single-surface product: with three platforms sharing data, any ambiguity in a spec turned into real inconsistencies fast. Clear documentation and a shared vocabulary between design and development kept the three systems actually agreeing with each other.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Max;